import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MutateOptions, useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { customAxiosErrorWithUnknownDataSchema } from '@breathelife/errors';
import { BlueprintModifier } from '@breathelife/insurance-form-builder';
import { logger } from '@breathelife/monitoring-frontend';
import { PublishQuestionnaireVersionResponse, QuestionnaireVersionFlag } from '@breathelife/types';
import {
  BlueprintCreation,
  BlueprintModification,
  BlueprintReorder,
  BlueprintReplacement,
  BlueprintUpdate,
  FieldBlueprint,
  FieldGroupBlueprint,
  FieldGroupPartIdentifier,
  FieldPartIdentifier,
  isBlueprintCreation,
  isBlueprintRemoval,
  isBlueprintReorder,
  isBlueprintUpdate,
  isFieldBlueprintCreation,
  isFieldBlueprintUpdate,
  isFieldGroupBlueprintCreation,
  isFieldGroupPartIdentifier,
  isFieldPartIdentifier,
  isSelectOptionBlueprintCreation,
  isSelectOptionPartIdentifier,
  PartIdentifier,
  QuestionnaireBlueprint,
  SectionBlueprint,
  SectionPartIdentifier,
  SelectOptionBlueprint,
  SelectOptionPartIdentifier,
  SerializedNodeIdToAnswerPathMap,
  SubsectionBlueprint,
  SubsectionPartIdentifier,
} from '@zinnia/form-engine-sdk';

import { queryClient } from '../..';
import {
  generateQuestionnaireNodeIdsFromBlueprint,
  QuestionnaireNodeIds,
} from '../../../Helpers/questionnaireEditor/questionnaireNodeIds';
import { QuestionnaireEditorContext } from '../../../Pages/Admin/Questionnaire/ContextProvider/QuestionnaireEditorContextProvider';
import { QuestionnaireVersionDataContext } from '../../../Pages/Admin/Questionnaire/ContextProvider/QuestionnaireVersionDataContextProvider';
import { QueryId } from '../../../ReactQuery/common/common.types';
import { notificationSlice } from '../../../ReduxStore/Notification/NotificationSlice';
import {
  createNewDraftQuestionnaireVersion,
  patchQuestionnaireVersionBlueprint,
  publishQuestionnaire,
  QuestionnaireVersionDetailWithNodeIdInfo,
  updateQuestionnaireVersion,
} from '../../../Services/QuestionnaireVersionService';

type PatchQuestionnaireMutationVariables = BlueprintModification;

type PatchQuestionnaireMutationContext = {
  previousQuestionnaireVersion: QuestionnaireVersionDetailWithNodeIdInfo | undefined;
};

function makeQueryId(questionnaireVersionId: string): [string, string] {
  return [QueryId.questionnaireVersion, questionnaireVersionId];
}

function isFieldBlueprintUpdateWithNewNodeIds(modification: BlueprintModification): boolean {
  return (
    isBlueprintUpdate(modification) &&
    isFieldBlueprintUpdate(modification) &&
    (modification.update.property === 'answerNodeId' ||
      modification.update.property === 'fieldType' ||
      modification.update.property === 'addressAutocompleteNodeId')
  );
}

function isAddFieldModification(modification: BlueprintModification): boolean {
  return isBlueprintCreation(modification) && isFieldBlueprintCreation(modification);
}

function isAddQuestionModification(modification: BlueprintModification): boolean {
  return isBlueprintCreation(modification) && isFieldGroupBlueprintCreation(modification);
}

function isAddSelectOptionModification(modification: BlueprintModification): boolean {
  return isBlueprintCreation(modification) && isSelectOptionBlueprintCreation(modification);
}

function isRemoveFieldModification(modification: BlueprintModification): boolean {
  return isBlueprintRemoval(modification) && isFieldPartIdentifier(modification.partIdentifier);
}

function isRemoveQuestionModification(modification: BlueprintModification): boolean {
  return isBlueprintRemoval(modification) && isFieldGroupPartIdentifier(modification.partIdentifier);
}

function isRemoveSelectOptionModification(modification: BlueprintModification): boolean {
  return isBlueprintRemoval(modification) && isSelectOptionPartIdentifier(modification.partIdentifier);
}

function getRefreshedQuestionnaireNodeIds(
  modification: BlueprintModification,
  questionnaireBlueprint?: QuestionnaireBlueprint,
  nodeIdToAnswerPath?: SerializedNodeIdToAnswerPathMap,
): QuestionnaireNodeIds | void {
  if (
    questionnaireBlueprint &&
    nodeIdToAnswerPath &&
    (isFieldBlueprintUpdateWithNewNodeIds(modification) ||
      isAddFieldModification(modification) ||
      isAddQuestionModification(modification) ||
      isAddSelectOptionModification(modification) ||
      isRemoveQuestionModification(modification) ||
      isRemoveFieldModification(modification) ||
      isRemoveSelectOptionModification(modification))
  ) {
    return generateQuestionnaireNodeIdsFromBlueprint(questionnaireBlueprint, nodeIdToAnswerPath);
  }
}

export function usePatchQuestionnaireMutation(
  questionnaireVersionId?: string,
  options?: UseMutationOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): UseMutationResult<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setIsMutating } = useContext(QuestionnaireEditorContext);

  function getOptimisticallyUpdatedBlueprint(
    currentQuestionnaireBlueprint: QuestionnaireBlueprint,
    modification: BlueprintModification,
  ): QuestionnaireBlueprint {
    const newBlueprint = { ...currentQuestionnaireBlueprint };
    if (isBlueprintCreation(modification)) {
      BlueprintModifier.createBlueprint(modification, newBlueprint);
    } else if (isBlueprintUpdate(modification)) {
      BlueprintModifier.updateBlueprint(modification, newBlueprint);
    } else if (isBlueprintRemoval(modification)) {
      BlueprintModifier.removeBlueprint(modification.partIdentifier, newBlueprint);
    } else if (isBlueprintReorder(modification)) {
      BlueprintModifier.reorderBlueprint(modification, newBlueprint);
    }
    return newBlueprint;
  }

  return useMutation<
    QuestionnaireBlueprint,
    unknown,
    PatchQuestionnaireMutationVariables,
    PatchQuestionnaireMutationContext
  >(
    async (modification) => {
      if (!questionnaireVersionId) {
        throw new Error('useMutation: Missing questionnaireVersionId');
      }
      return patchQuestionnaireVersionBlueprint(questionnaireVersionId, modification);
    },
    {
      onMutate: async (modification) => {
        options?.onMutate?.(modification);
        setIsMutating?.(true);
        if (!questionnaireVersionId) {
          return;
        }
        const queryId = makeQueryId(questionnaireVersionId);
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(queryId);

        const questionnaireVersionQueryData = queryClient.getQueryData<
          QuestionnaireVersionDetailWithNodeIdInfo | undefined
        >(queryId);

        // Optimistically update to the new value
        queryClient.setQueryData<QuestionnaireVersionDetailWithNodeIdInfo | undefined>(queryId, (queryData) => {
          if (queryData?.blueprint) {
            const updatedBlueprint = getOptimisticallyUpdatedBlueprint(queryData?.blueprint, modification);
            return { ...queryData, blueprint: updatedBlueprint };
          }
          return queryData;
        });

        return { previousQuestionnaireVersion: questionnaireVersionQueryData };
      },
      onError: async (error, variables, context) => {
        logger.error(error);

        const revertOptimisticUIChanges = context?.previousQuestionnaireVersion && questionnaireVersionId;
        if (revertOptimisticUIChanges) {
          queryClient.setQueryData(makeQueryId(questionnaireVersionId), context.previousQuestionnaireVersion);
        }

        if (error instanceof AxiosError && error?.response?.data?.message) {
          dispatch(notificationSlice.actions.setError({ message: error.response.data.message }));
        } else {
          dispatch(notificationSlice.actions.setError({ message: t('notifications.failedToSaveQuestionnaire') }));
        }

        if (options?.onError) {
          await options.onError(error, variables, context);
        }
      },
      onSuccess: async (data, variables, context) => {
        if (!questionnaireVersionId) {
          return;
        }

        queryClient.setQueryData<QuestionnaireVersionDetailWithNodeIdInfo | undefined>(
          makeQueryId(questionnaireVersionId),
          (queryData) => {
            const { blueprint, nodeIdToAnswerPath } = queryData || {};
            const updatedNodeIds = getRefreshedQuestionnaireNodeIds(variables, blueprint, nodeIdToAnswerPath);
            dispatch(notificationSlice.actions.setSuccess({ message: t('notifications.saveQuestionnaireSuccess') }));
            return queryData
              ? {
                  ...queryData,
                  blueprint: data,
                  questionnaireNodeIds: updatedNodeIds || queryData.questionnaireNodeIds,
                }
              : queryData;
          },
        );

        if (options?.onSuccess) {
          await options.onSuccess(data, variables, context);
        }
      },
      onSettled: () => {
        setIsMutating?.(false);
      },
    },
  );
}

export function useUpdateQuestionnaireElementBlueprint<T extends BlueprintUpdate>(
  questionnaireVersionId?: string,
  options?: MutateOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): (modification: T) => Promise<void> {
  const patchMutation = usePatchQuestionnaireMutation(questionnaireVersionId);
  return async (modification: T) => {
    return patchMutation.mutate(modification, {
      ...options,
      onError: async (error, variables, context) => {
        logger.error(error);

        if (options?.onError) {
          await options.onError(error, variables, context);
        }
      },
    });
  };
}

interface CreateBlueprintFunc {
  (newBlueprint: SectionBlueprint, parentPartIdentifier: null): Promise<void>;
  (newBlueprint: SubsectionBlueprint, parentPartIdentifier?: SectionPartIdentifier): Promise<void>;
  (newBlueprint: FieldGroupBlueprint, parentPartIdentifier?: SubsectionPartIdentifier): Promise<void>;
  (newBlueprint: FieldBlueprint, parentPartIdentifier?: FieldGroupPartIdentifier): Promise<void>;
  (newBlueprint: SelectOptionBlueprint, parentPartIdentifier?: FieldPartIdentifier): Promise<void>;
}

export function useAddQuestionnaireElementBlueprint(
  questionnaireVersionId?: string,
  options?: MutateOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): CreateBlueprintFunc {
  const patchMutation = usePatchQuestionnaireMutation(questionnaireVersionId);

  const createBlueprint: CreateBlueprintFunc = async (newBlueprint: any, parentPartIdentifier: any) => {
    const blueprintCreation: BlueprintCreation = {
      partIdentifier: parentPartIdentifier || null,
      create: { blueprint: newBlueprint },
    };

    return patchMutation.mutate(blueprintCreation, {
      ...options,
      onError: async (error, variables, context) => {
        logger.error(error);

        if (options?.onError) {
          await options.onError(error, variables, context);
        }
      },
    });
  };

  return createBlueprint;
}

export function useRemoveQuestionnaireElementBlueprint(
  questionnaireVersionId?: string,
  options?: MutateOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): (partIdentifier: PartIdentifier) => Promise<void> {
  const patchMutation = usePatchQuestionnaireMutation(questionnaireVersionId);

  return async (partIdentifier: PartIdentifier) => {
    await patchMutation.mutate(
      {
        partIdentifier,
        remove: true,
      },
      {
        ...options,
        async onError(error, variables, context) {
          logger.error(error);

          if (options?.onError) {
            await options.onError(error, variables, context);
          }
        },
      },
    );
  };
}

export function useReplaceQuestionnaireElementBlueprint(
  questionnaireVersionId?: string,
  options?: MutateOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): (blueprintReplacement: BlueprintReplacement) => Promise<void> {
  const patchMutation = usePatchQuestionnaireMutation(questionnaireVersionId);

  return async (blueprintReplacement) => {
    await patchMutation.mutate(blueprintReplacement, {
      ...options,
      async onError(error, variables, context) {
        logger.error(error);

        if (options?.onError) {
          await options.onError(error, variables, context);
        }
      },
    });
  };
}

interface UpdateOrderFunc {
  (options: {
    sourcePartIdentifier: SectionPartIdentifier;
    targetSiblingPartIdentifier?: SectionPartIdentifier;
  }): Promise<void>;
  (options: {
    sourcePartIdentifier: SubsectionPartIdentifier;
    targetSiblingPartIdentifier?: SubsectionPartIdentifier;
    targetParentPartIdentifier?: SectionPartIdentifier;
  }): Promise<void>;
  (options: {
    sourcePartIdentifier: FieldGroupPartIdentifier;
    targetSiblingPartIdentifier?: FieldGroupPartIdentifier;
    targetParentPartIdentifier?: SubsectionPartIdentifier;
  }): Promise<void>;
  (options: {
    sourcePartIdentifier: FieldPartIdentifier;
    targetParentPartIdentifier?: FieldGroupPartIdentifier;
    targetSiblingPartIdentifier?: FieldPartIdentifier;
  }): Promise<void>;
  (options: {
    sourcePartIdentifier: SelectOptionPartIdentifier;
    targetSiblingPartIdentifier?: SelectOptionPartIdentifier;
    targetParentPartIdentifier?: FieldPartIdentifier;
  }): Promise<void>;
}

export function useUpdateQuestionnaireElementBlueprintOrder(
  questionnaireVersionId?: string,
  options?: MutateOptions<QuestionnaireBlueprint, unknown, PatchQuestionnaireMutationVariables>,
): UpdateOrderFunc {
  const patchMutation = usePatchQuestionnaireMutation(questionnaireVersionId);

  const updateOrder: UpdateOrderFunc = async ({
    sourcePartIdentifier,
    targetParentPartIdentifier,
    targetSiblingPartIdentifier,
  }: {
    sourcePartIdentifier: any;
    targetSiblingPartIdentifier?: any;
    targetParentPartIdentifier?: any;
  }) => {
    const blueprintReorder: BlueprintReorder = {
      partIdentifier: sourcePartIdentifier,
      reorder: {
        newPreviousSiblingIdentifier: targetSiblingPartIdentifier,
        parentIdentifier: targetParentPartIdentifier,
      },
    };
    await patchMutation.mutate(blueprintReorder, {
      ...options,
      onError: async (error, variables, context) => {
        logger.error(error);

        if (options?.onError) {
          await options.onError(error, variables, context);
        }
      },
    });
  };
  return updateOrder;
}

type PublishQuestionnaireVersionVariables = {
  description: string;
};

export function usePublishQuestionnaireVersionMutation(
  options?: UseMutationOptions<PublishQuestionnaireVersionResponse, unknown, PublishQuestionnaireVersionVariables>,
): UseMutationResult<PublishQuestionnaireVersionResponse, unknown, PublishQuestionnaireVersionVariables> {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { questionnaireVersionId } = useContext(QuestionnaireVersionDataContext);
  return useMutation<PublishQuestionnaireVersionResponse, unknown, PublishQuestionnaireVersionVariables>(
    async ({ description }) => {
      if (!questionnaireVersionId) {
        throw new Error('No questionnaire Id');
      }
      const result = await publishQuestionnaire(questionnaireVersionId, description);

      if (result.tag === 'success') {
        dispatch(notificationSlice.actions.setSuccess({ message: t('notifications.publishQuestionnaireSuccess') }));

        void queryClient.invalidateQueries([QueryId.questionnaireVersions]);
        void queryClient.invalidateQueries([QueryId.allQuestionnaireVersions]);
        void queryClient.invalidateQueries([QueryId.questionnaireVersion, questionnaireVersionId]);
        void queryClient.invalidateQueries([QueryId.allQuestionnairesNodeIds]);
      }

      return result;
    },
    {
      ...options,
      onError: async (err, variables, context) => {
        const parsedError = customAxiosErrorWithUnknownDataSchema.safeParse(err);

        if (!parsedError.success) {
          dispatch(notificationSlice.actions.setError({ message: t('notifications.failedToPublishQuestionnaire') }));
          return;
        }

        const errorId = parsedError.data.response?.data?.errors?.errorId;

        if (errorId) {
          dispatch(notificationSlice.actions.setError({ message: t('notifications.failedToPublishQuestionnaire') }));
        }
        logger.error(err);

        if (options?.onError) {
          await options.onError(err, variables, context);
        }
      },
    },
  );
}

type NewDraftQuestionnaireVariables = {
  description: string;
};

type NewDraftQuestionnaireResult = {
  questionnaireVersionId: string;
};

export function useNewDraftQuestionnaireVersionMutation(
  options?: UseMutationOptions<NewDraftQuestionnaireResult, unknown, NewDraftQuestionnaireVariables>,
): UseMutationResult<NewDraftQuestionnaireResult, unknown, NewDraftQuestionnaireVariables> {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { questionnaireVersionData } = useContext(QuestionnaireVersionDataContext);
  return useMutation<NewDraftQuestionnaireResult, unknown, NewDraftQuestionnaireVariables>(
    async ({ description }) => {
      if (!questionnaireVersionData) {
        throw new Error('No questionnaire Id');
      }
      const { questionnaireId } = questionnaireVersionData;
      const draftId = await createNewDraftQuestionnaireVersion(questionnaireId, description);

      dispatch(notificationSlice.actions.setSuccess({ message: t('notifications.newDraftQuestionnaireSuccess') }));

      void queryClient.invalidateQueries([QueryId.questionnaireVersions]);

      return { questionnaireVersionId: draftId };
    },
    {
      ...options,
      onError: async (err, variables, context) => {
        notificationSlice.actions.setError({
          message: t('notifications.failedToCreateNewDraftQuestionnaire'),
        });
        logger.error(err);

        if (options?.onError) {
          await options.onError(err, variables, context);
        }
      },
    },
  );
}

export function useUpdateQuestionnaireVersionMutation(
  questionnaireVersionId?: string,
  options?: UseMutationOptions<QuestionnaireVersionFlag[], unknown, { flags: QuestionnaireVersionFlag[] }>,
): UseMutationResult<QuestionnaireVersionFlag[], unknown, { flags: QuestionnaireVersionFlag[] }> {
  if (!questionnaireVersionId) {
    throw new Error('No questionnaire version Id');
  }
  return useMutation<QuestionnaireVersionFlag[], unknown, { flags: QuestionnaireVersionFlag[] }>(
    async ({ flags }) => {
      return await updateQuestionnaireVersion(questionnaireVersionId, { flags });
    },
    {
      ...options,
      onSuccess: async (data) => {
        queryClient.setQueryData<QuestionnaireVersionDetailWithNodeIdInfo | undefined>(
          [QueryId.questionnaireVersion, questionnaireVersionId],
          (queryData) => {
            if (queryData) {
              return {
                ...queryData,
                flags: data,
              };
            }
            return queryData;
          },
        );
      },
      onError: async (err, variables, context) => {
        logger.error(err);
        await options?.onError?.(err, variables, context);
      },
    },
  );
}
